import { Schema } from "@effect/schema";
import { satisfies } from "effect/Function";

/**
 * Naming convention:
 *
 * For generic oracles like uniswap, chainlink, redstone
 * - kebab case (lowercased)
 *
 * For token-specific oracles
 * - use exact token symbol
 * - add "-oracle" suffix at the end
 */
export const OracleKey = Schema.Literal(
  "uniswap-v3",
  "balancer",
  "chainlink",
  "uniswap-v3-v2",
  "uniswap-v3-v3",
  "wstETH-oracle",
  "gOHM-oracle",
  "dia",
  "curve-lp",
  "crvUSD-oracle",
  "redstone",
  "chronicle",
  "stEUR-oracle",
  "PT-eETH-oracle",
  "PT-ezETH-renzo-oracle",
  "PT-ezETH-chainlink-oracle",
  "PT-rswETH-oracle",
  "PT-weETH-oracle",
  "PT-rsETH-oracle",
  "PT-pufETH-oracle",
  "PT-USDe-oracle",
  "PT-LBTC-oracle",
  "woETH-oracle",
  "api3",
  "wUSD+-oracle",
  "uniBTC-oracle",
  "pzETH-oracle",
  "amphrETH-oracle",
  "rstETH-oracle",
  "re7LRT-oracle",
  "dlcBTC-oracle",
  "sUSDz-oracle",
  "wSuperOETHb-oracle",
  "pumpBTC-oracle"
).annotations({ identifier: "OracleKey" });

export type OracleKey = typeof OracleKey.Type;
export const oracleKeys = OracleKey.literals;
export const isOracleKey = Schema.is(OracleKey);

export const OracleRiskLevel = Schema.Literal("low", "moderate").annotations({
  identifier: "OracleRiskLevel",
});
export type OracleRiskLevel = typeof OracleRiskLevel.Type;
export const oracleRiskLevels = OracleRiskLevel.literals;
export const isOracleRiskLevel = Schema.is(OracleRiskLevel);

export const NATIVE_ORACLE_KEY = satisfies<OracleKey>()("chainlink");

interface Oracle {
  label: string;
  riskLevel: OracleRiskLevel;
}

export const oracleMap = satisfies<Record<OracleKey, Oracle>>()({
  "uniswap-v3": {
    label: "Uniswap V3",
    riskLevel: "moderate",
  },
  balancer: {
    label: "Balancer",
    riskLevel: "moderate",
  },
  chainlink: {
    label: "Chainlink",
    riskLevel: "low",
  },
  "uniswap-v3-v2": {
    label: "Uniswap V3",
    riskLevel: "moderate",
  },
  "uniswap-v3-v3": {
    label: "Uniswap V3",
    riskLevel: "moderate",
  },
  "wstETH-oracle": {
    label: "Chainlink",
    riskLevel: "low",
  },
  "gOHM-oracle": {
    label: "Chainlink",
    riskLevel: "low",
  },
  dia: {
    label: "DIA",
    riskLevel: "moderate",
  },
  "curve-lp": {
    label: "Custom Oracle",
    riskLevel: "low",
  },
  "crvUSD-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  redstone: {
    label: "RedStone",
    riskLevel: "low",
  },
  chronicle: {
    label: "Chronicle",
    riskLevel: "low",
  },
  "stEUR-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "PT-eETH-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "PT-ezETH-renzo-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "PT-ezETH-chainlink-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "PT-rswETH-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "PT-weETH-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "PT-rsETH-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "PT-USDe-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "PT-pufETH-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "PT-LBTC-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "woETH-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  api3: {
    label: "API3",
    riskLevel: "low",
  },
  "wUSD+-oracle": {
    label: "Custom Oracle",
    riskLevel: "low",
  },
  "uniBTC-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "pzETH-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "amphrETH-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "rstETH-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "re7LRT-oracle": {
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "dlcBTC-oracle":{
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "sUSDz-oracle":{
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "wSuperOETHb-oracle":{
    label: "Custom Oracle",
    riskLevel: "moderate",
  },
  "pumpBTC-oracle":{
    label: "Custom Oracle",
    riskLevel: "moderate",
  }
});

export function getOracle(oracleKey: OracleKey): Oracle {
  return oracleMap[oracleKey];
}

export const UNKNOWN_ORACLE: Oracle = {
  label: "Custom Oracle",
  riskLevel: "moderate",
};
